// externals
import { connect } from 'react-redux';

// selectors
import * as tradeInSelectors from '../../../store/mmd/tradeIn';
import * as leadFormSelectors from '../../../store/mmd/leadForm';
import { getHasBeenSent } from '../../../store/mmd/offerSelectors';
import { isDealerUser } from '../../../store/mmd/widgetSelectors';
// actions
import * as tradeInActionCreators from '../../../store/actionCreators';

// consts/enums
import * as tradeInRoutes from '../../../utils/routes';
import { getTradeInSelectors, getUniqueEstimateOption, processStepperRoutes } from '../../../utils/tradeInDecisionLogic';
import { LOAN_BALANCE_TRADE_IN_STEP, STEPPER_ROUTES, stepperRouteMap } from '../../../utils/constants';
import { isShowroomExperience } from '../../../utils/configUtils';

// components
import LoanBalanceUI from './LoanBalanceUI';

// interfaces/types
import { ILoanBalanceUIStateProps, ILoanBalanceUIDispatchProps } from './LoanBalanceInterfaces';
import { IRootProps } from '../../../components/RootProps';
import { TRADEIN_ESTIMATE_METHODS, LOAN_STATUS, TRADEIN_SOURCE_TYPES } from '../constants';
import { StepperRoute } from '../../../types';

const mapStateToProps = (state: any, ownProps: IRootProps): ILoanBalanceUIStateProps => {
    const { tradeIn } = tradeInSelectors.getTradeInState(state, ownProps.tradeIndex);

    const isAmdLienPayoffQuoteSpOnSPEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);
    const { vehicle } = tradeIn;

    const vehicleName = `${vehicle.year} ${vehicle.make.name} ${vehicle.model.name}`;
    const loanStatus = tradeIn?.hasOpenLoan ?? null;

    const pageTitle = 'Trade-In Vehicle';
    const pageSubTitle = 'Determine how much your current vehicle, is worth, and apply that value to your new vehicle.';

    const isSalesView = isShowroomExperience(ownProps.config);
    const tradeInSource = tradeIn?.source;
    const selectedStepperRoute: StepperRoute[] = stepperRouteMap[tradeInSource] || STEPPER_ROUTES.loanBalance;

    const stepperRoutes = processStepperRoutes(
        state,
        selectedStepperRoute,
        isSalesView,
        isAmdLienPayoffQuoteSpOnSPEnabled,
        ownProps.tradeIndex
    );

    const tradeInAssertionSelectors = getTradeInSelectors(state, isSalesView);
    const { hasUniqueOption: hasUniqueEstimateOption } = getUniqueEstimateOption(tradeInAssertionSelectors, isSalesView);

    return {
        vehicle,
        vehicleName,
        hasOpenLoan: loanStatus,
        pageTitle,
        pageSubTitle,
        stepperRoutes,
        hasUniqueEstimateOption,
        tradeInSource,
        tradeIndex: ownProps.tradeIndex,
        urlPath: ownProps.tradeInRoute
    };
};

export const mapDispatchToProps = (dispatch: any, ownProps: IRootProps): ILoanBalanceUIDispatchProps => {
    const getAppraisalBackRoute = (
        isSalesView: boolean,
        showAppraisalValueScreenSalesView: boolean,
        hasUniqueOption: boolean
    ): string => {
        const isAppraisalValueScreenHidden = isSalesView && !showAppraisalValueScreenSalesView;
        const defaultAppraisalRoute = isAppraisalValueScreenHidden
            ? tradeInRoutes.TRADE_IN_DECISION
            : tradeInRoutes.TRADE_MANUAL_ENTRY;

        return isAppraisalValueScreenHidden && hasUniqueOption ? tradeInRoutes.TRADE_VEHICLE_INFO : defaultAppraisalRoute;
    };

    return {
        dispatchAnalytics: (type: string, description: string) => {
            dispatch(tradeInActionCreators.dispatchAnalytics(type, description));
        },
        goToRoute: (route: string) => {
            dispatch(tradeInActionCreators.updateTradeInCurrentLocation(route));
        },
        next: (hasOpenLoan?: boolean) => {
            // If user selects "No" (no open loan), execute any pending ICO logic before going to summary
            if (!hasOpenLoan) {
                dispatch((dispatch: any, getState: any) => {
                    const state = getState();
                    const isAmdLienPayoffEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);
                    const isLeadFormSubmitted = leadFormSelectors.isLeadFormSubmittedSuccess(state);
                    const isLeadFormEnabled = tradeInSelectors.shouldShowLeadForm(state);
                    const leadFormSubmittedAndOfferSent = getHasBeenSent(state) && isLeadFormSubmitted;
                    const getIsDealerUser = isDealerUser(state);
                    // Only execute deferred logic if feature flag is enabled
                    if (isAmdLienPayoffEnabled) {
                        const tradeInComponent = tradeInSelectors.getTradeInComponent(state);
                        const tradeAddedPayload = (tradeInComponent as any)?.pendingIcoTradeAddedPayload;
                        const tradeCompletedPayload = (tradeInComponent as any)?.pendingIcoTradeCompletedPayload;

                        // Execute deferred logic for EXTERNAL_TRADE_ADDED
                        if (tradeAddedPayload) {
                            const { data, actionType } = tradeAddedPayload;
                            dispatch({ type: actionType, payload: data });
                            dispatch({ type: 'ICO_TRADE_IN_COMPLETED', payload: data });
                        }

                        // Execute deferred logic for EXTERNAL_TRADE_COMPLETED
                        if (tradeCompletedPayload) {
                            dispatch({ type: 'ICO_TRADE_IN_COMPLETED', payload: tradeCompletedPayload });
                            dispatch(tradeInActionCreators.tradeInEstimateSuccess());
                            dispatch(tradeInActionCreators.externalIcoCompleted(true));
                            dispatch(tradeInActionCreators.updateTradeInValuation());
                            dispatch(tradeInActionCreators.sendAddICOAnalytics());
                        }

                        // Clear the pending payloads
                        if (tradeAddedPayload || tradeCompletedPayload) {
                            dispatch({ type: 'CLEAR_ICO_PENDING_PAYLOADS' });
                        }
                    }
                    // Clear any previous entry for loan balance in case user went back and changed selection from "Yes" to "No"
                    dispatch(tradeInActionCreators.updateTradeInBalanceOwed(0));
                    // Route to summary
                     if (leadFormSubmittedAndOfferSent || getIsDealerUser || !isLeadFormEnabled) {
                             dispatch(tradeInActionCreators.saveTradeIn());
                     }
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_SUMMARY));
                });
            } else {
                // Route to loan balance options for "Yes" selection
                dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_LOAN_BALANCE_OPTIONS));
            }
        },
        goBack: (hasUniqueEstimateOption: boolean, tradeInSource: string) => {
            dispatch((dispatch: any, getState: any) => {
                const state = getState();
                const isSalesView = isShowroomExperience(ownProps.config);
                const tradeInAssertionSelectors = getTradeInSelectors(state, isSalesView);

                if (tradeInSource === TRADEIN_ESTIMATE_METHODS.kbb || tradeInSource === TRADEIN_ESTIMATE_METHODS.SD) {
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_EQUIPMENT_OPTIONS));
                } else if (tradeInSource === TRADEIN_ESTIMATE_METHODS.USER) {
                    const backRoute = getAppraisalBackRoute(
                        isSalesView,
                        tradeInAssertionSelectors.isShowAppraisalValueScreenSalesView,
                        hasUniqueEstimateOption
                    );
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(backRoute));
                } else if (tradeInSource === TRADEIN_ESTIMATE_METHODS.ico) {
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_IN_ICO));
                } else if (tradeInSource === TRADEIN_SOURCE_TYPES.subarugtp && !hasUniqueEstimateOption) {
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_IN_DECISION));
                } else {
                    dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_VEHICLE_INFO));
                }
            });
        },
        onLoanStatusChange: (hasOpenLoan: boolean) => {
            dispatch(tradeInActionCreators.setOpenLoanBalance(hasOpenLoan));

            // Analytics tracking
            const status = hasOpenLoan ? LOAN_STATUS.YES : LOAN_STATUS.NO;
            dispatch(tradeInActionCreators.dispatchAnalytics('DR_TRADEIN_LOAN_STATUS_SELECTED', `Loan status selected: ${status}`));
            dispatch(tradeInActionCreators.cdlHasLoanBalanceClicked(status));
        },
        removeTrade: () => {
            // Clear any pending ICO payloads when user removes trade-in (only if feature flag is enabled)
            dispatch((dispatch: any, getState: any) => {
                const state = getState();
                const isAmdLienPayoffEnabled = tradeInSelectors.isAmdLienPayoffQuoteEnabled(state);

                if (isAmdLienPayoffEnabled) {
                    dispatch({ type: 'CLEAR_ICO_PENDING_PAYLOADS' });
                }

                // fire CDL analytics for cancel, then remove trade and route to the trade-in start
                dispatch(tradeInActionCreators.cdlCancelTradeInButtonClicked(LOAN_BALANCE_TRADE_IN_STEP));
                dispatch(tradeInActionCreators.updateTradeInCurrentLocation(tradeInRoutes.TRADE_IN));
                dispatch(tradeInActionCreators.removeTradeIn());
            });
        },
        cdlBackButtonClicked: () => {
            dispatch(tradeInActionCreators.cdlBackButtonClicked(LOAN_BALANCE_TRADE_IN_STEP));
        },
        cdlCancelTradeInButtonClicked: () => {
            dispatch(tradeInActionCreators.cdlCancelTradeInButtonClicked(LOAN_BALANCE_TRADE_IN_STEP));
        }
    };
};

const LoanBalance = connect(mapStateToProps, mapDispatchToProps)(LoanBalanceUI);

export default LoanBalance;
